import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, output, ViewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Snackbar } from '../../../services/snackbar/snackbar.service';
import {
  QueryListItem,
  QueryListRequest,
} from '../../../models/api/query/query-list';
import { Action } from '../../../models/core/action';
import { BaseComponent } from '../../base/base.component';
import { ActionButtonComponent } from '../../core/buttons/action-button.component';
import { ButtonComponent } from '../../core/buttons/button.component';
import { ToolbarButtonComponent } from '../../core/buttons/toolbar-button.component';
import { CheckboxComponent } from '../../core/controls/checkbox.component';
import { SelectComponent } from '../../core/controls/select.component';
import { ActionRowComponent } from '../../core/panels/action-row.component';
import { EmptyStateComponent } from '../../core/panels/empty-state.component';
import { MenuComponent } from '../../core/panels/menu.component';
import { ProgressbarComponent } from '../../core/labels/progressbar.component';
import { ColumnComponent } from '../../core/table/column.component';
import { TableComponent } from '../../core/table/table.component';
import { CopyQueryDialogComponent } from '../copy-query-dialog.component';
import { QUERY_LIST_PAGE_SIZE_OPTIONS } from '../query-list-page-size-options';

@Component({
  selector: 'app-query-list',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TableComponent,
    ColumnComponent,
    ButtonComponent,
    ActionButtonComponent,
    ToolbarButtonComponent,
    SelectComponent,
    CheckboxComponent,
    ActionRowComponent,
    CopyQueryDialogComponent,
    EmptyStateComponent,
    MenuComponent,
    ProgressbarComponent,
  ],
  template: `
    <div>
      <div
        class="tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-end tw-gap-2 sm:tw-gap-0 tw-mb-4"
      >
        <app-checkbox
          class="tw-w-full sm:tw-w-1/4 tw-min-w-0"
          [control]="includePublicControl"
          label="Include Public"
        />
        <app-select
          class="tw-w-full sm:tw-w-1/4 tw-min-w-0"
          [control]="lastRunWithinControl"
          label="Last Run Within"
          [options]="dateRangeOptions"
          optionLabel="label"
          optionValue="value"
          [filter]="false"
          [showClear]="false"
        />
        <app-button
          class="tw-w-full sm:tw-w-1/4 tw-pb-2 tw-self-end"
          label="Search"
          icon="fa-light fa-search"
          (click)="search()"
        />
      </div>

      <div class="tw-flex tw-flex-row tw-w-full">
        @if (loading) {
          <app-empty-state
            height="300px"
            icon="fa-light fa-magnifying-glass"
            label="Loading queries"
            message="Retrieving queries"
            class="tw-w-full"
          >
            <div class="tw-py-4">
              <app-progressbar />
            </div>
          </app-empty-state>
        } @else {
          <app-table
            class="tw-w-full"
            [dataSource]="queries"
            [showRefresh]="true"
            (refresh)="loadQueries(true)"
            [showSelection]="true"
            [selectionKey]="'queryId'"
            [isRowSelectable]="isQuerySelectable"
            [showFilter]="true"
            [sortField]="sortField"
            [sortOrder]="sortOrder"
            recordLabel="Query"
            recordLabelPlural="Queries"
            dataKey="queryId"
            [pageSize]="pageSize"
            [pageSizeOptions]="pageSizeOptions"
            [persistPaginationSettings]="true"
            tableId="query-view-queries"
            (selectedEvent)="onSelectionChange($event)"
          >
            <app-column name="owner" label="Owner" [sort]="true" />
            <app-column name="name" label="Name" [sort]="true" />
            <app-column name="description" label="Description" />
            <app-column name="queryTypeName" label="Type" [sort]="true" />
            <app-column name="lastRun" label="Last Run" [sort]="true">
              <ng-template #cell let-row>
                {{ row.lastRun | date: 'short' }}
              </ng-template>
            </app-column>
            <app-column name="actions" label="Actions">
              <ng-template #cell let-row>
                @if (getRowActions(row).length) {
                  <span class="md:tw-hidden">
                    <app-action-button
                      [menu]="rowMenu"
                      [row]="row"
                      (click)="openRowMenu($event, row)"
                    />
                  </span>
                }
                <div class="tw-hidden md:tw-flex tw-gap-1">
                  @if (row.isOwner && row.queryType !== 'Banking') {
                    <app-toolbar-button
                      type="Compact"
                      icon="fa-light fa-edit"
                      ariaLabel="Edit"
                      (click)="editQuery(row)"
                    />
                  }
                  @if (row.queryType !== 'Banking') {
                    <app-toolbar-button
                      type="Compact"
                      icon="fa-light fa-copy"
                      ariaLabel="Copy"
                      (click)="copyQuery(row)"
                    />
                  }
                  @if (row.isOwner && row.queryType !== 'Banking') {
                    <app-toolbar-button
                      type="Compact"
                      icon="fa-light fa-calendar"
                      ariaLabel="Schedule"
                      (click)="scheduleQuery(row)"
                    />
                  }
                  @if (row.isOwner) {
                    <app-toolbar-button
                      type="Compact"
                      icon="fa-light fa-trash-can"
                      ariaLabel="Delete"
                      (click)="deleteQuery(row)"
                    />
                  }
                </div>
              </ng-template>
            </app-column>
          </app-table>
        }
      </div>

      <app-menu [items]="rowMenuItems" #rowMenu (click)="onRowAction($event)" />

      @if (selectedQueries.length > 0) {
        <app-action-row class="tw-mt-4">
          <span left1>{{ selectedQueries.length }} selected</span>
          @if (selectedQueries.length === 1 && selectedQueries[0].isOwner) {
            <app-button
              right1
              label="Schedule Selected"
              icon="fa-light fa-calendar"
              emphasis="Low"
              (click)="scheduleSelected()"
            />
          }
          @if (selectedQueries.length === 1 && selectedQueries[0].isOwner) {
            <app-button
              right2
              label="Delete Selected"
              icon="fa-light fa-trash-can"
              emphasis="Low"
              (click)="deleteSelected()"
            />
          }
          @if (selectedQueries.length !== 1 || !selectedQueries[0].isOwner) {
            <app-button
              right1
              label="Delete Selected"
              icon="fa-light fa-trash-can"
              emphasis="Low"
              (click)="deleteSelected()"
            />
          }
          @if (selectedQueries.length !== 1 || !selectedQueries[0].isOwner) {
            <span right2></span>
          }
        </app-action-row>
      }

      <app-copy-query-dialog #copyDialog (copy)="onCopyConfirm($event)" />
    </div>
  `,
  styles: ``,
})
export class QueryListComponent extends BaseComponent implements OnInit {
  private snackbar = inject(Snackbar);

  @ViewChild('copyDialog')
  copyDialog!: CopyQueryDialogComponent;

  @ViewChild('rowMenu')
  rowMenu: MenuComponent;

  editRequested = output<QueryListItem>();
  scheduleQueryRequested = output<QueryListItem>();

  queries: QueryListItem[] = [];
  selectedQueries: QueryListItem[] = [];
  loading = false;
  pageSize = 10;
  pageSizeOptions = [...QUERY_LIST_PAGE_SIZE_OPTIONS];
  sortField = 'lastRun';
  sortOrder = -1;

  includePublicControl = new FormControl(false);
  lastRunWithinControl = new FormControl('30d');

  isQuerySelectable = (query: QueryListItem): boolean => query.isOwner;

  dateRangeOptions = [
    { label: '30 Days', value: '30d' },
    { label: '60 Days', value: '60d' },
    { label: '6 Months', value: '6mo' },
    { label: '1 Year', value: '1yr' },
    { label: 'All', value: 'all' },
  ];

  rowMenuItems: Action[] = [];

  private skipDeleteConfirmation = false;

  getRowActions(row: QueryListItem): Action[] {
    const actions: Action[] = [];
    if (row.isOwner && row.queryType !== 'Banking') {
      actions.push({ label: 'Edit', key: 'edit' });
    }
    if (row.queryType !== 'Banking') {
      actions.push({ label: 'Copy', key: 'copy' });
    }
    if (row.isOwner && row.queryType !== 'Banking') {
      actions.push({ label: 'Schedule', key: 'schedule' });
    }
    if (row.isOwner) {
      actions.push({ label: 'Delete', key: 'delete' });
    }
    return actions;
  }

  async ngOnInit(): Promise<void> {
    this.dispose.subs = this.includePublicControl.valueChanges.subscribe(() => {
      void this.loadQueries(false);
    });

    await this.loadDeleteConfirmationPreference();
    await this.loadQueries(true);
  }

  private async loadDeleteConfirmationPreference(): Promise<void> {
    const prefs = await this.api.preferences.getPreferences(
      {
        type: 'QUERY',
        key: 'DONT_CONFIRM_DELETE',
        value: '',
      },
      { cache: true },
    );
    this.skipDeleteConfirmation = prefs.some(
      (pref) => pref.value?.toUpperCase() === 'Y',
    );
  }

  private async saveDeleteConfirmationPreference(): Promise<void> {
    await this.api.preferences.setPreference({
      type: 'QUERY',
      key: 'DONT_CONFIRM_DELETE',
      value: 'Y',
    });
    this.skipDeleteConfirmation = true;
  }

  async loadQueries(showLoading = true): Promise<void> {
    if (showLoading) {
      this.loading = true;
    }
    try {
      const request: QueryListRequest = {
        page: 1,
        pageSize: 1000,
        lastRunWithin: this.lastRunWithinControl.value ?? 'all',
        includePublic: this.includePublicControl.value ?? false,
        sortField: this.sortField,
        sortDirection: this.sortOrder === 1 ? 'asc' : 'desc',
      };
      const response = await this.api.query.getQueries(request);
      this.queries = response.items;
      this.selectedQueries = [];
    } finally {
      this.loading = false;
    }
  }

  search(): void {
    void this.loadQueries(false);
  }

  onSelectionChange(selected: QueryListItem[]): void {
    this.selectedQueries = selected;
  }

  editQuery(query: QueryListItem): void {
    this.editRequested.emit(query);
  }

  scheduleQuery(query: QueryListItem): void {
    this.scheduleQueryRequested.emit(query);
  }

  scheduleSelected(): void {
    if (this.selectedQueries.length === 1 && this.selectedQueries[0].isOwner) {
      this.scheduleQueryRequested.emit(this.selectedQueries[0]);
    }
  }

  private queryToCopy: QueryListItem | null = null;

  copyQuery(query: QueryListItem): void {
    this.queryToCopy = query;
    this.copyDialog.show(query.name, query.description ?? '');
  }

  openRowMenu(event: Event, row: QueryListItem): void {
    this.rowMenuItems = this.getRowActions(row);
    this.rowMenu.items = this.rowMenuItems;
    this.rowMenu.toggle(event, row);
  }

  onRowAction(action: Action): void {
    const query = (this.rowMenu?.data ?? action.data) as QueryListItem;
    if (!query) return;

    switch (action.key) {
      case 'edit':
        this.editQuery(query);
        break;
      case 'copy':
        this.copyQuery(query);
        break;
      case 'schedule':
        this.scheduleQuery(query);
        break;
      case 'delete':
        this.deleteQuery(query);
        break;
    }
  }

  async onCopyConfirm(result: {
    name: string;
    description: string;
  }): Promise<void> {
    const query = this.queryToCopy;
    if (!query) {
      return;
    }
    this.copyDialog.setLoading(true);
    try {
      const response = await this.api.query.copyQuery(query.queryId, {
        newName: result.name,
        newDescription: result.description,
      });
      if (response.success) {
        this.queryToCopy = null;
        this.copyDialog.close();
        this.snackbar.show(
          'Query copied successfully',
          'Success',
          'fa-light fa-copy',
        );
        await this.loadQueries();
      } else {
        this.snackbar.show(
          response.message || 'Failed to copy query.',
          'Error',
          'fa-light fa-xmark',
        );
        this.copyDialog.close();
      }
    } catch {
      this.snackbar.show(
        'An error occurred while copying the query.',
        'Error',
        'fa-light fa-xmark',
      );
      this.copyDialog.close();
    } finally {
      this.copyDialog.setLoading(false);
    }
  }

  deleteQuery(query: QueryListItem): void {
    this.showDeleteConfirmation(async () => {
      const response = await this.api.query.deleteQuery(query.queryId);
      if (response.success) {
        await this.loadQueries();
      }
    });
  }

  deleteSelected(): void {
    this.showDeleteConfirmation(async () => {
      for (const query of this.selectedQueries) {
        await this.api.query.deleteQuery(query.queryId);
      }
      this.selectedQueries = [];
      await this.loadQueries();
    });
  }

  private showDeleteConfirmation(onConfirm: () => Promise<void>): void {
    if (this.skipDeleteConfirmation) {
      void onConfirm();
      return;
    }

    this.confirm.show({
      label: 'viaOne',
      message:
        'This will delete the selected Queries/Reports. Click OK to continue with delete or click Cancel to abort delete.',
      yesLabel: 'OK',
      noLabel: 'Cancel',
      showDoNotShowAgain: true,
      confirm: async (_data, doNotShowAgain) => {
        if (doNotShowAgain) {
          await this.saveDeleteConfirmationPreference();
        }
        await onConfirm();
      },
    });
  }
}
